import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Mail, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

type EmailFormData = { newEmail: string };

interface ChangeEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ChangeEmailModal({ isOpen, onClose }: ChangeEmailModalProps) {
  const { t } = useTranslation(['profile', 'common']);
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const emailSchema = useMemo(() => z.object({
    newEmail: z
      .string()
      .min(1, t('profile:modals.changeEmail.emailRequired'))
      .email(t('profile:modals.changeEmail.emailInvalid')),
  }), [t]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      reset();
      setError(null);
      setSuccess(false);
    }
  }, [isOpen, reset]);

  if (!isOpen) return null;

  const onSubmit = async (data: EmailFormData) => {
    if (data.newEmail === user?.email) {
      setError(t('profile:modals.changeEmail.sameEmail'));
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const { error } = await supabase.auth.updateUser({
        email: data.newEmail,
      });

      if (error) {
        if (error.message.includes('already registered')) {
          setError(t('profile:modals.changeEmail.alreadyRegistered'));
        } else {
          setError(error.message);
        }
        return;
      }

      setSuccess(true);
    } catch {
      setError(t('profile:modals.changeEmail.unexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setError(null);
    reset();
    onClose();
  };

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={handleClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pp-orange/20 rounded-full flex items-center justify-center">
                <Mail className="w-5 h-5 text-pp-orange" />
              </div>
              <h2>{t('profile:modals.changeEmail.title')}</h2>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content space-y-4">
            {success ? (
              // Success State
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-pp-teal/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-pp-teal" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  {t('profile:modals.changeEmail.successTitle')}
                </h3>
                <p className="text-white/70 mb-4">
                  {t('profile:modals.changeEmail.successMessage')}
                </p>
                <div className="bg-pp-orange/20 border border-pp-orange/30 rounded-[15px] p-4 text-sm text-white">
                  <strong>{t('common:important', { defaultValue: 'Wichtig' })}:</strong> {t('profile:modals.changeEmail.importantNote')}
                </div>
              </div>
            ) : (
              // Form State
              <>
                <p className="text-sm text-white/70">
                  {t('profile:modals.changeEmail.description')}
                </p>

                {/* Current Email */}
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    {t('profile:modals.changeEmail.currentEmail')}
                  </label>
                  <div className="flex items-center gap-3 p-3 bg-white/10 rounded-[10px] border border-white/20">
                    <Mail className="w-5 h-5 text-white/50" />
                    <span className="text-white">{user?.email}</span>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-[10px] flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-300">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      {t('profile:modals.changeEmail.newEmail')}
                    </label>
                    <input
                      {...register('newEmail')}
                      type="email"
                      placeholder={t('profile:modals.changeEmail.newEmailPlaceholder')}
                      autoComplete="email"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-[10px] text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
                    />
                    {errors.newEmail && (
                      <p className="mt-1 text-sm text-red-400">{errors.newEmail.message}</p>
                    )}
                  </div>
                </form>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            {success ? (
              <button
                onClick={handleClose}
                className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90"
              >
                {t('common:actions.close')}
              </button>
            ) : (
              <>
                <button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30 disabled:opacity-50"
                >
                  {t('common:actions.cancel')}
                </button>
                <button
                  onClick={handleSubmit(onSubmit)}
                  disabled={isSubmitting}
                  className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    t('profile:modals.changeEmail.submitButton')
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}
